import React, { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import UserLayout from "@/components/layouts/UserLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Users, Share2, TrendingUp, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import ReferralShareCard from "@/components/ReferralShareCard";

const UserReferrals = () => {
  const { user, getAuthHeaders } = useAuth();
  const [referralData, setReferralData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReferrals();
  }, []);

  const fetchReferrals = async () => {
    try {
      const res = await axios.get(`${API}/users/referrals`, {
        headers: getAuthHeaders(),
      });
      setReferralData(res.data);
    } catch (error) {
      toast.error("Failed to load referrals");
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <UserLayout title="Referrals">
        <div className="animate-pulse space-y-4">
          <div className="h-40 bg-gray-200 rounded-2xl" />
          <div className="h-60 bg-gray-200 rounded-2xl" />
        </div>
      </UserLayout>
    );
  }

  const directReferrals = referralData?.direct_referrals || [];
  const mlmLevels = referralData?.mlm_levels || [];

  return (
    <UserLayout title="Referrals">
      {/* Share Card */}
      <div className="mb-6">
        <ReferralShareCard
          referralCode={user?.referral_code}
          referralLink={referralData?.referral_link || ""}
        />
      </div>

      {/* MLM Levels */}
      <Card className="border-0 shadow-sm mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-secondary-500" />
            Commission Structure
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {mlmLevels.length > 0 ? (
              mlmLevels.map((level) => (
                <div
                  key={level.level}
                  className="p-4 bg-gray-50 rounded-xl text-center"
                >
                  <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-primary-700 font-bold">L{level.level}</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">
                    {level.commission_percent}%
                  </p>
                  <p className="text-sm text-gray-500">Level {level.level} Commission</p>
                </div>
              ))
            ) : (
              <div className="col-span-3 text-center py-8 text-gray-500">
                Commission structure not configured
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Referral Tree */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-primary-600" />
            Your Network ({directReferrals.length} Direct Referrals)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {directReferrals.length > 0 ? (
            <div className="space-y-3">
              {directReferrals.map((referral) => (
                <div
                  key={referral.id}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-xl"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                      <span className="text-primary-700 font-semibold">
                        {referral.name?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{referral.name}</p>
                      <p className="text-sm text-gray-500">{referral.mobile}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge
                      className={
                        referral.is_verified
                          ? "bg-green-100 text-green-700"
                          : "bg-yellow-100 text-yellow-700"
                      }
                    >
                      {referral.is_verified ? "Verified" : "Pending"}
                    </Badge>
                    <p className="text-xs text-gray-400 mt-1">
                      {new Date(referral.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No Referrals Yet
              </h3>
              <p className="text-gray-500 mb-4 max-w-md mx-auto">
                Share your referral link with friends and family to start building your network and earning commissions!
              </p>
              <Button
                onClick={() => {
                  navigator.clipboard.writeText(referralData?.referral_link || "");
                  toast.success("Link copied!");
                }}
                className="bg-secondary-500 !text-primary-900 hover:bg-secondary-400 rounded-full"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share Now
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Visual Tree Representation */}
      {directReferrals.length > 0 && (
        <Card className="border-0 shadow-sm mt-6">
          <CardHeader>
            <CardTitle>Referral Tree</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center">
              {/* You at the top */}
              <div className="flex flex-col items-center p-4 bg-primary-100 border-2 border-primary-500 rounded-xl min-w-[140px]">
                <div className="w-12 h-12 bg-primary-500 rounded-full flex items-center justify-center mb-2">
                  <span className="text-white font-bold text-lg">
                    {user?.name?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <p className="font-semibold text-primary-800">{user?.name}</p>
                <p className="text-xs text-primary-600">You</p>
              </div>

              {/* Connector */}
              <div className="w-0.5 h-8 bg-gray-300" />
              <ChevronDown className="w-5 h-5 text-gray-400 -mt-1" />

              {/* Level 1 Referrals */}
              <div className="flex flex-wrap justify-center gap-4 mt-4">
                {directReferrals.slice(0, 5).map((ref) => (
                  <div
                    key={ref.id}
                    className="flex flex-col items-center p-3 bg-white border-2 border-gray-200 rounded-xl min-w-[120px]"
                  >
                    <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center mb-2">
                      <span className="text-gray-600 font-semibold">
                        {ref.name?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <p className="font-medium text-gray-800 text-sm">{ref.name}</p>
                    <Badge className="bg-green-100 text-green-700 text-xs mt-1">
                      Level 1
                    </Badge>
                  </div>
                ))}
              </div>
              
              {directReferrals.length > 5 && (
                <p className="text-gray-500 text-sm mt-4">
                  +{directReferrals.length - 5} more referrals
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </UserLayout>
  );
};

export default UserReferrals;
